import React from 'react';
import { TEXT_COLOR } from '../../theme';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import StarIcon from '@mui/icons-material/Star';
import { getText } from '../common/MultiLanguageText';
import { getDynamicLabel } from '../../utils/dynamicFields';
import { performanceText } from '../common/Texts';
import DynamicFieldInput from '../DynamicFieldInput';

const CARD_SX = {
    bgcolor: '#fff',
    border: '1px solid #E1E4E8',
    borderRadius: '14px',
    boxShadow: '0 1px 2px rgba(15,17,35,0.04)',
    p: 3,
};

const LABEL_SX = {
    display: 'block',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    color: TEXT_COLOR,
    mb: 0.75,
};

const STAT_VALUE_SX = {
    fontSize: '22px',
    fontWeight: 700,
    color: TEXT_COLOR,
    lineHeight: 1.25,
};

function FieldLabel({ children }) {
    return <Typography sx={LABEL_SX} component="div">{children}</Typography>;
}

function StatBox({ label, value }) {
    return (
        <Box>
            <FieldLabel>{label}</FieldLabel>
            <Typography sx={STAT_VALUE_SX}>{value || '—'}</Typography>
        </Box>
    );
}

function formatDuration(seconds) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

export default function EmployeeDataCard({
    tr, language,
    questionnaire, isHighPot,
    nombre, dob, user_generic_id, id_label,
    posicion, areafunc, business_unit, grupo_interno,
    nivelCalc, subnivelCalc, nivelLabel, subnivelLabel, nivelPot,
    duration_seconds, status, performance,
    positions, departments, business_units, grupos_internos,
    dynamic_fields, dynamic_answers, dynamic_errors,
    generic_id_error,
    handleChange, handleDynamicChange,
}) {
    const title = getText(questionnaire, 'title', language);
    const subtitle = getText(questionnaire, 'subtitle', language);
    const currentLevel = nivelCalc ? `${nivelCalc}${subnivelCalc}` : `${nivelLabel}${subnivelLabel}`;

    return (
        <Box sx={CARD_SX}>
            {/* Encabezado */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: TEXT_COLOR, mb: 0.5 }}>
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography sx={{ fontSize: '13px', color: TEXT_COLOR }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                {isHighPot && (
                    <Chip
                        icon={<StarIcon sx={{ fontSize: '14px !important', color: '#1F3A8A !important' }} />}
                        label={tr('high_potential')}
                        size="small"
                        sx={{
                            bgcolor: '#EEF2FB',
                            color: TEXT_COLOR,
                            fontWeight: 600,
                            fontSize: '12px',
                            borderRadius: '8px',
                            flexShrink: 0,
                        }}
                    />
                )}
            </Box>

            {/* Fila 1: Nombre, Fecha de nacimiento, ID */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 2.5 }}>
                <Box>
                    <FieldLabel>{tr('name')}</FieldLabel>
                    <TextField fullWidth size="small" name="nombre" value={nombre} onChange={handleChange} />
                </Box>
                <Box>
                    <FieldLabel>{tr('dob')}</FieldLabel>
                    <TextField
                        fullWidth size="small" name="dob" type="date" value={dob}
                        onChange={handleChange}
                        slotProps={{ inputLabel: { shrink: true } }}
                    />
                </Box>
                <Box>
                    <FieldLabel>{id_label || 'ID'}</FieldLabel>
                    <TextField
                        fullWidth size="small" name="user_generic_id" value={user_generic_id}
                        onChange={handleChange}
                        error={!!generic_id_error}
                        helperText={generic_id_error || undefined}
                    />
                </Box>
            </Box>

            {/* Fila 2: Selectores organizacionales */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 2.5 }}>
                <Box>
                    <FieldLabel>{tr('business_unit')}</FieldLabel>
                    <FormControl fullWidth size="small">
                        <Select name="business_unit" value={business_unit} onChange={handleChange}>
                            {business_units.map(bu => (
                                <MenuItem key={bu.id} value={bu.id}>{getText(bu, 'name', language)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box>
                    <FieldLabel>{tr('position')}</FieldLabel>
                    <FormControl fullWidth size="small">
                        <Select name="posicion" value={posicion} onChange={handleChange}>
                            {positions.map(p => (
                                <MenuItem key={p.id} value={p.id}>{getText(p, 'name', language)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box>
                    <FieldLabel>{tr('area')}</FieldLabel>
                    <FormControl fullWidth size="small">
                        <Select name="areafunc" value={areafunc} onChange={handleChange}>
                            {departments.map(d => (
                                <MenuItem key={d.id} value={d.id}>{getText(d, 'name', language)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box>
                    <FieldLabel>{tr('internal_group')}</FieldLabel>
                    <FormControl fullWidth size="small">
                        <Select name="grupo_interno" value={grupo_interno} onChange={handleChange}>
                            {grupos_internos.map(gi => (
                                <MenuItem key={gi.id} value={gi.id}>{getText(gi, 'name', language)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {/* Campos dinámicos */}
            {dynamic_fields.length > 0 && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 2.5 }}>
                    {dynamic_fields.map(f => (
                        <Box key={f.key}>
                            <FieldLabel>
                                {getDynamicLabel(f, language)}{f.required ? ' *' : ''}
                            </FieldLabel>
                            <Box sx={{ '& .MuiFormControl-root': { width: '100%', mt: 0 } }}>
                                <DynamicFieldInput
                                    field={f}
                                    value={dynamic_answers[f.key]}
                                    error={dynamic_errors[f.key]}
                                    onChange={handleDynamicChange(f.key)}
                                    language={language}
                                />
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}

            {/* Stats de sólo lectura */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 2,
                pt: 2.5,
                mt: 0.5,
                borderTop: '1px solid #E1E4E8',
                mb: 2.5,
            }}>
                <StatBox label={tr('current_level')} value={currentLevel} />
                <StatBox label={tr('potential')} value={nivelPot} />
                <StatBox label={tr('survey_time')} value={formatDuration(duration_seconds)} />
                <StatBox
                    label={tr('state')}
                    value={status === 'S' ? tr('not_answered') : tr('answered')}
                />
            </Box>

            {/* Desempeño */}
            <Box sx={{ maxWidth: 220 }}>
                <FieldLabel>{tr('performance')}</FieldLabel>
                <FormControl fullWidth size="small">
                    <Select name="performance" value={performance} onChange={handleChange}>
                        <MenuItem value=""><em>—</em></MenuItem>
                        <MenuItem value={1}>{performanceText(1, tr)}</MenuItem>
                        <MenuItem value={2}>{performanceText(2, tr)}</MenuItem>
                        <MenuItem value={3}>{performanceText(3, tr)}</MenuItem>
                    </Select>
                </FormControl>
            </Box>
        </Box>
    );
}
